'use client';

import { useState, useMemo, useEffect } from 'react';
import Link from 'next/link';
import {
  Search,
  Filter,
  AlertTriangle,
  Calendar,
  ChevronRight,
  X,
  Shield,
  ArrowUpDown,
  Building2,
  Loader2,
} from 'lucide-react';

interface KEVEntry {
  cveID: string;
  vendorProject: string;
  product: string;
  vulnerabilityName: string;
  dateAdded: string;
  shortDescription: string;
  requiredAction: string;
  dueDate: string;
  knownRansomwareCampaignUse: string;
  notes?: string;
}

interface KEVCatalog {
  title: string;
  catalogVersion: string;
  dateReleased: string;
  count: number;
  vulnerabilities: KEVEntry[];
}

type SortField = 'dateAdded' | 'dueDate' | 'vendor' | 'cveID';
type SortDirection = 'asc' | 'desc';

export default function KEVCatalogClient() {
  const [catalog, setCatalog] = useState<KEVCatalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [ransomwareOnly, setRansomwareOnly] = useState(false);
  const [sortField, setSortField] = useState<SortField>('dateAdded');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [displayCount, setDisplayCount] = useState(50);

  // Fetch data on mount
  useEffect(() => {
    async function fetchKEV() {
      try {
        const response = await fetch('/api/tools/cve/kev');
        if (!response.ok) throw new Error('Failed to fetch KEV catalog');
        const data: KEVCatalog = await response.json();
        setCatalog(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    }
    fetchKEV();
  }, []);

  const vulnerabilities = catalog?.vulnerabilities || [];
  const totalCount = catalog?.count || 0;

  const vendors = useMemo(() => {
    return [...new Set(vulnerabilities.map((v) => v.vendorProject))].sort();
  }, [vulnerabilities]);

  const ransomwareCount = useMemo(() => {
    return vulnerabilities.filter((v) => v.knownRansomwareCampaignUse === 'Known').length;
  }, [vulnerabilities]);

  const recentCount = useMemo(() => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return vulnerabilities.filter((v) => new Date(v.dateAdded) >= thirtyDaysAgo).length;
  }, [vulnerabilities]);

  const filteredAndSorted = useMemo(() => {
    let filtered = vulnerabilities;

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (v) =>
          v.cveID.toLowerCase().includes(term) ||
          v.vendorProject.toLowerCase().includes(term) ||
          v.product.toLowerCase().includes(term) ||
          v.vulnerabilityName.toLowerCase().includes(term) ||
          v.shortDescription.toLowerCase().includes(term)
      );
    }

    // Apply vendor filter
    if (vendorFilter) {
      filtered = filtered.filter((v) => v.vendorProject === vendorFilter);
    }

    // Apply ransomware filter
    if (ransomwareOnly) {
      filtered = filtered.filter((v) => v.knownRansomwareCampaignUse === 'Known');
    }

    // Sort
    filtered = [...filtered].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'dateAdded':
          comparison = new Date(a.dateAdded).getTime() - new Date(b.dateAdded).getTime();
          break;
        case 'dueDate':
          comparison = new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
          break;
        case 'vendor':
          comparison = a.vendorProject.localeCompare(b.vendorProject);
          break;
        case 'cveID':
          comparison = a.cveID.localeCompare(b.cveID);
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [vulnerabilities, searchTerm, vendorFilter, ransomwareOnly, sortField, sortDirection]);

  const displayedVulnerabilities = filteredAndSorted.slice(0, displayCount);
  const hasMore = displayCount < filteredAndSorted.length;

  const clearFilters = () => {
    setSearchTerm('');
    setVendorFilter('');
    setRansomwareOnly(false);
  };

  const hasActiveFilters = searchTerm || vendorFilter || ransomwareOnly;

  const isDueDatePassed = (dueDate: string) => new Date(dueDate) < new Date();
  const isDueDateSoon = (dueDate: string) => {
    const due = new Date(dueDate);
    const now = new Date();
    const sevenDays = new Date();
    sevenDays.setDate(sevenDays.getDate() + 7);
    return due >= now && due <= sevenDays;
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading KEV catalog...</span>
      </div>
    );
  }

  // Error state
  if (error || !catalog) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-red-800 dark:text-red-200 mb-2">
          Unable to Load KEV Catalog
        </h2>
        <p className="text-red-700 dark:text-red-300">
          {error || 'The CISA KEV Catalog is temporarily unavailable. Please try again later.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
            <div>
              <div className="text-2xl font-bold text-red-900 dark:text-red-100">
                {totalCount.toLocaleString()}
              </div>
              <div className="text-sm text-red-700 dark:text-red-300">Total KEV Entries</div>
            </div>
          </div>
        </div>
        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-orange-600 dark:text-orange-400" />
            <div>
              <div className="text-2xl font-bold text-orange-900 dark:text-orange-100">
                {ransomwareCount.toLocaleString()}
              </div>
              <div className="text-sm text-orange-700 dark:text-orange-300">Ransomware Related</div>
            </div>
          </div>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Calendar className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            <div>
              <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                {recentCount.toLocaleString()}
              </div>
              <div className="text-sm text-blue-700 dark:text-blue-300">Added Last 30 Days</div>
            </div>
          </div>
        </div>
        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Building2 className="w-8 h-8 text-purple-600 dark:text-purple-400" />
            <div>
              <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                {vendors.length.toLocaleString()}
              </div>
              <div className="text-sm text-purple-700 dark:text-purple-300">Vendors Affected</div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        {/* Search Bar */}
        <div className="flex gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search CVE ID, vendor, product, or description..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg transition-colors ${
              showFilters || hasActiveFilters
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <Filter className="w-5 h-5" />
            <span className="hidden sm:inline">Filters</span>
            {hasActiveFilters && (
              <span className="px-1.5 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                {[searchTerm, vendorFilter, ransomwareOnly].filter(Boolean).length}
              </span>
            )}
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Vendor Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Vendor/Project
                </label>
                <select
                  value={vendorFilter}
                  onChange={(e) => setVendorFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="">All Vendors ({vendors.length})</option>
                  {vendors.map((vendor) => (
                    <option key={vendor} value={vendor}>
                      {vendor}
                    </option>
                  ))}
                </select>
              </div>

              {/* Ransomware Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ransomware Campaign Use
                </label>
                <label className="flex items-center gap-3 cursor-pointer p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                  <input
                    type="checkbox"
                    checked={ransomwareOnly}
                    onChange={(e) => setRansomwareOnly(e.target.checked)}
                    className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                  />
                  <Shield className="w-4 h-4 text-orange-500" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Show only ransomware-related
                  </span>
                </label>
              </div>

              {/* Sort */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Sort By
                </label>
                <div className="flex gap-2">
                  <select
                    value={sortField}
                    onChange={(e) => setSortField(e.target.value as SortField)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                  >
                    <option value="dateAdded">Date Added</option>
                    <option value="dueDate">Due Date</option>
                    <option value="vendor">Vendor</option>
                    <option value="cveID">CVE ID</option>
                  </select>
                  <button
                    onClick={() => setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                    title={sortDirection === 'asc' ? 'Ascending' : 'Descending'}
                  >
                    <ArrowUpDown className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
              >
                <X className="w-4 h-4" />
                Clear all filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Showing {displayedVulnerabilities.length.toLocaleString()} of{' '}
          {filteredAndSorted.length.toLocaleString()} vulnerabilities
          {filteredAndSorted.length !== totalCount && (
            <span className="text-gray-500"> (filtered from {totalCount.toLocaleString()})</span>
          )}
        </p>
      </div>

      {/* Vulnerability List */}
      <div className="space-y-3">
        {displayedVulnerabilities.map((vuln) => (
          <Link
            key={vuln.cveID}
            href={`/tools/security/cve-lookup/${vuln.cveID.toLowerCase()}`}
            className={`block bg-white dark:bg-gray-800 border rounded-lg p-4 hover:shadow-md transition-all group ${
              vuln.knownRansomwareCampaignUse === 'Known'
                ? 'border-orange-300 dark:border-orange-700 hover:border-orange-400 dark:hover:border-orange-600'
                : 'border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-600'
            }`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                {/* CVE ID and Badges */}
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className="font-mono font-semibold text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300">
                    {vuln.cveID}
                  </span>
                  {vuln.knownRansomwareCampaignUse === 'Known' && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-xs font-semibold rounded-full">
                      <Shield className="w-3 h-3" />
                      Ransomware
                    </span>
                  )}
                  {isDueDatePassed(vuln.dueDate) && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs font-semibold rounded-full">
                      <AlertTriangle className="w-3 h-3" />
                      Overdue
                    </span>
                  )}
                  {!isDueDatePassed(vuln.dueDate) && isDueDateSoon(vuln.dueDate) && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 text-xs font-semibold rounded-full">
                      <Calendar className="w-3 h-3" />
                      Due Soon
                    </span>
                  )}
                </div>

                {/* Vendor and Product */}
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {vuln.vendorProject}
                  </span>
                  {' • '}
                  {vuln.product}
                </div>

                {/* Vulnerability Name */}
                <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-2 line-clamp-1">
                  {vuln.vulnerabilityName}
                </h3>

                {/* Description */}
                <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3">
                  {vuln.shortDescription}
                </p>

                {/* Dates */}
                <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    Added: {new Date(vuln.dateAdded).toLocaleDateString()}
                  </span>
                  <span
                    className={`flex items-center gap-1 ${
                      isDueDatePassed(vuln.dueDate)
                        ? 'text-red-600 dark:text-red-400 font-semibold'
                        : isDueDateSoon(vuln.dueDate)
                        ? 'text-yellow-600 dark:text-yellow-400 font-semibold'
                        : ''
                    }`}
                  >
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Due: {new Date(vuln.dueDate).toLocaleDateString()}
                  </span>
                </div>
              </div>

              {/* Arrow */}
              <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-500 flex-shrink-0 mt-1 transition-colors" />
            </div>
          </Link>
        ))}
      </div>

      {/* Load More */}
      {hasMore && (
        <div className="text-center pt-4">
          <button
            onClick={() => setDisplayCount((c) => c + 50)}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
          >
            Load More ({filteredAndSorted.length - displayCount} remaining)
          </button>
        </div>
      )}

      {/* Empty State */}
      {filteredAndSorted.length === 0 && (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            No vulnerabilities found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Try adjusting your search or filters
          </p>
          <button
            onClick={clearFilters}
            className="text-blue-600 dark:text-blue-400 hover:underline"
          >
            Clear all filters
          </button>
        </div>
      )}
    </div>
  );
}
